import { motion } from "framer-motion";
import { BadgeCheck, MapPin } from "lucide-react";

const STATS = [
  { value: "10+", label: "Years running multi-state portfolios" },
  { value: "78", label: "Communities across 16 states overseen today" },
  { value: "6,000+", label: "Units & lots overseen career-wide" },
  { value: "12%", label: "Month-over-month NOI lift delivered at PMG" },
];

const TIMELINE = [
  { years: "Present", role: "Director of Training and Development", org: "Lasso Capital", note: "Leads training and development across a 50-community, 13-state portfolio — the same programs distilled into OPERA360 TRAIN." },
  { years: "2025 — Present", role: "Regional Director", org: "brightSTAR Communities", note: "28 manufactured-home communities across 3 states. Owns Fair Housing training programs and state-specific policy infrastructure." },
  { years: "2023 — 2025", role: "General Manager", org: "Inspire Communities", note: "1,700 lots across 5 communities in Las Vegas. Cut delinquency 2% and lifted occupancy 5% month-over-month." },
  { years: "2022 — 2023", role: "Regional Asset Manager", org: "PMG (The Robinson Group)", note: "2,122 units across 12 properties. Designed portfolio-wide training and operational standards; +12% NOI MoM." },
  { years: "2018 — 2021", role: "General Manager", org: "Lakeshore Management", note: "1,200 lots across 3 communities. Capital projects, owner's reports, and a 19 five-star review run in 5 months." },
  { years: "2013 — 2017", role: "Regional Property Manager", org: "Rait Residential", note: "852 units, 36 employees. Built the '10 Pledges of Customer Service' and record resident satisfaction." },
];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

export const Founder = () => (
  <section id="founder" className="relative py-24 sm:py-32 border-t border-slate-200" data-testid="founder-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
      <motion.div {...reveal} className="lg:col-span-5">
        <div className="relative mb-9 w-60 sm:w-72">
          <div className="absolute -bottom-2.5 -right-2.5 h-full w-full rounded-md border border-cadmium/40" aria-hidden="true" />
          <img
            src="/founder.jpg"
            alt="Christopher Love, Founder of OPERA360"
            data-testid="founder-headshot"
            className="relative h-72 sm:h-80 w-full rounded-md object-cover object-top"
          />
        </div>
        <p className="eyebrow mb-4 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-cadmium" /> The Operator Behind OPERA360
        </p>
        <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
          Built by someone who has <span className="font-editorial italic font-normal text-sky-600">walked your lots.</span>
        </h2>
        <p className="mt-6 font-editorial italic text-lg sm:text-xl text-slate-900/85 leading-relaxed">
          "Every module in OPERA360 is a system I had to build by hand — Fair Housing programs, onboarding tracks, SOP
          libraries, walk standards — across thousands of units. Now it's packaged so no operator has to rebuild it
          from tribal knowledge again."
        </p>
        <p className="mt-6 text-base font-semibold text-slate-900">Christopher Love</p>
        <p className="text-sm text-muted-foreground">Founder, OPERA360</p>
        <div className="mt-6 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-sm border border-moss/40 bg-moss/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-green-700">
            <BadgeCheck size={11} /> Licensed Real Estate Professional
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-sm border border-cadmium/40 bg-cadmium/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-sky-600">
            <BadgeCheck size={11} /> Fair Housing Program Builder
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <MapPin size={11} /> Yardi · RentManager · SOP Design
          </span>
        </div>
      </motion.div>

      <div className="lg:col-span-7">
        <motion.div {...reveal} className="grid grid-cols-2 gap-4 mb-10">
          {STATS.map((s, i) => (
            <div key={s.label} data-testid={`founder-stat-${i}`} className="technical-card p-5">
              <p className="font-mono text-3xl font-semibold text-slate-900">{s.value}</p>
              <p className="mt-1.5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground leading-snug">{s.label}</p>
            </div>
          ))}
        </motion.div>
        <motion.ol {...reveal} className="relative border-l border-slate-200 ml-2 space-y-7">
          {TIMELINE.map((t, i) => (
            <li key={t.org} data-testid={`founder-timeline-${i}`} className="relative pl-7">
              <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-cadmium" />
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">{t.years}</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {t.role} · <span className="text-sky-600">{t.org}</span>
              </p>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{t.note}</p>
            </li>
          ))}
        </motion.ol>
      </div>
    </div>
  </section>
);
